<template>
  <div class="page-content">
    <v-progress-circular
      v-if="!showData && !showEpicError"
      indeterminate
      color="red"
    />
    <v-alert
      v-if="showEpicError"
      title="Error while querying Epic"
      variant="outlined"
      type="warning"
      prominent
      border="top"
    >
      <ul v-if="errorMessages && errorMessages.length > 0">
        <li
          v-for="(errorMessage, index) in errorMessages"
          :key="index"
        >
          <span v-if="typeof errorMessage === 'string'">
            {{ errorMessage }}
          </span>
          <!-- If the error message is an object with more config then render that.-->
          <span v-else>
            <span v-if="errorMessage.message">{{ errorMessage.message }}</span>
            <span v-if="errorMessage.errors">
              <ul class="error-list">
                <li
                  v-for="(error, idex) in errorMessage.errors"
                  :key="idex"
                >
                  {{ error }}
                </li>
              </ul>
            </span>
          </span>
        </li>
      </ul>
    </v-alert>
    <div v-if="showData">
      <div class="card summary-card">
        <PatientDetails
          :patient="patient"
          :patientType="_patientType"
        />
        <div class="media-body flex-row align-items-center">
          <h3 class="summary-name">
            Please confirm what to include in this data transfer
          </h3>
        </div>
        <br>
        <div class="row">
          <div class="col col-4 pt-2">
            <label class="px-1">
              Select Episode of Care
            </label>
            <v-select
              v-model="selectedEpisodeOfCare"
              class="connect-input"
              variant="outlined"
              :items="episodeOfCareOptions"
              :itemProps="true"
              @update:model-value="clearCheckboxes()"
            />
            <!-- </div> -->
          </div>
        </div>
        <div class="row">
          <template
            v-for="(item, index) in sections"
            :key="index"
          >
            <div
              v-if="_selectedEpisode && _selectedEpisode.display && item.patient_types.includes(_selectedEpisode.display.toLowerCase())"
              class="col-4"
            >
              <div
                class="card cardcheckbox"
                :class="!item.enabled ? 'disable' : ''"
                @click.stop="selectItem(item)"
              >
                <v-checkbox
                  v-model="item.checked"
                  :disabled="!item.enabled"
                  hideDetails
                  color="#0060DF"
                  :label="item.title"
                  @:change="onSelect()"
                />
              </div>
            </div>
          </template>
        </div>
        <div v-if="invalidPostRequest">
          <span class="invalidPostRequest">
            {{ invalidPostRequestMessage }}
          </span>
        </div>
        <div>
          <v-hover>
            <template #default="{ isHovering, props }">
              <div class="d-flex flex-row align-center">
                <v-btn
                  :disabled="!_sendToOATSValidated"
                  v-bind="props"
                  class="text-none  mb-2 ml-3 px-4"
                  height="33"
                  :color="isHovering ? '#146c43' : '#007F43'"
                  rounded="0"
                  @click="postPatientData"
                >
                  Send to OATS
                </v-btn>
                <div class="mb-2 px-3">
                  Last Attempt: {{ lastAttempt ?? 'None' }}
                </div>
              </div>
            </template>
          </v-hover>
          &nbsp;
          <v-progress-circular
            v-if="postRequestInProgress"
            indeterminate
            color="red"
          />
          &nbsp;
          <div
            v-if="dataPosted === 'error'"
            class="fade-list-item notification notification-error notification-inline button-area"
          >
            <p>
              <v-icon
                icon="mdi-alert-circle-outline"
                color="red"
              />
              {{ dataPostedErrorMessage }}
            </p>
          </div>
        </div>
        <br v-if="dataPosted !== 'success'">
        <hr v-if="dataPosted === 'success'">
        <span
          v-if="dataPosted === 'success'"
          class="dataactivitymessage"
        >
          <strong>Data transfer activity:</strong>
        </span>
        <v-card
          v-if="dataPosted === 'success'"
          color="#FFF3CD"
        >
          <v-card-text>
            <div
              v-for="(item, index) in handlersResponseData"
              :key="index"
            >
              <div class="alertMessage">
                <b>&#x2022;</b> {{ getHandler(item.handler) }}
                <v-icon
                  v-if="item.execution.ok"
                  icon="mdi-check-circle-outline"
                  color="green"
                />
                <v-icon
                  v-if="!item.execution.ok"
                  icon="mdi-alert"
                  color="red"
                />
              </div>
              <ul class="messagesList">
                <li
                  v-for="( message, rank ) in item.execution.result.messages"
                  :key="rank"
                >
                  {{ message.message }}
                </li>
              </ul>
            </div>
          </v-card-text>
        </v-card>

        <!-- <v-switch
          v-model="showPreview"
          label="Show Preview"
          color="primary"
        /> -->

        <br>
      

        <DemographicsSectionDetails
          v-if="isSectionChecked( 'Demographics and Contact Information' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <DemographicsSectionDetailsEMR
          v-if="isSectionChecked( 'Demographics and Contact Information' ) && _patientType === 'Recipient' "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
        />
        <DemographicsSectionDetailsDonorEMROATS
          v-if="isSectionChecked( 'Demographics and Contact Information' ) && _patientType !== 'Recipient' "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
        />
        <ClinicalSectionDetails
          v-if="isSectionChecked( 'General Clinical Information' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <ClinicalSectionDetailsWithOATS
          v-if="isSectionChecked( 'General Clinical Information' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
        />
        <DonorClinicalSection
          v-if="isSectionChecked( 'Clinical Information' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <DonorClinicalSectionEMROATS
          v-if="isSectionChecked( 'Clinical Information' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
        />
        <VirologySectionDetails
          v-if="isSectionChecked( 'Virology' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <VirologySectionDetailsEMROATS
          v-if="isSectionChecked( 'Virology' ) && _patientType === 'Recipient' "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
        />
        <VirologySectionDetailsDonorEMROATS
          v-if="isSectionChecked( 'Virology' ) && _patientType !== 'Recipient' "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
        />
        <ReferralConsultationSectionDetails
          v-if="isSectionChecked( 'Referral/Consultation/Medical Assessment' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <ReferralConsultationSectionOATSDetails
          v-if="isSectionChecked( 'Referral/Consultation/Medical Assessment' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
          :organ="'Kidney'"
        />
        <KidneySpecificSectionDetails
          v-if="isSectionChecked( 'Kidney Specific Details' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <KidneySpecificSectionEMROATS
          v-if="isSectionChecked( 'Kidney Specific Details' ) && _patientType === 'Recipient' "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
        />
        <DialysisSectionDetails
          v-if="isSectionChecked( 'Dialysis' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <DialysisSectionDetailsEMROATS
          v-if="isSectionChecked( 'Dialysis' ) && _patientType === 'Recipient' "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
        />
        <DonorAcceptabilitySectionDetails
          v-if="isSectionChecked( 'Donor Acceptability Criteria' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <DonorAcceptabilitySectionDetailsEMROATS
          v-if="isSectionChecked( 'Donor Acceptability Criteria' ) && _patientType === 'Recipient' "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
          :organ="'Kidney'"
        />
        <TransplantSectionDetails
          v-if="isSectionChecked( 'Transplant Details' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <PostTransplantSectionDetails
          v-if="isSectionChecked( 'Post-Transplant Details' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <PostTransplantSectionDetailsEMROATS
          v-if="isSectionChecked( 'Post-Transplant Details' ) && _patientType === 'Recipient' "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
          :organ="'Kidney'"
        />
        <RecipientDeathSectionDetails
          v-if="isSectionChecked( 'Recipient Death' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <RecipientDeathSectionDetailsEMROATS
          v-if="isSectionChecked( 'Recipient Death' ) && _patientType === 'Recipient' "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
        />
        <DonationInformationSection
          v-if="isSectionChecked( 'Donation Information' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <DonationInformationSectionEMROATS
          v-if="isSectionChecked( 'Donation Information' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
          :organ="'Kidney'"
        />
        <LinkToRecipientSection
          v-if="isSectionChecked( 'Link to Recipient' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <LinkToRecipientSectionEMROATS
          v-if="isSectionChecked( 'Link to Recipient' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
        />
        <RecoveryDetailsSection
          v-if="isSectionChecked( 'Recovery Details' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
        />
        <RecoveryDetailsSectionEMROATS
          v-if="isSectionChecked( 'Recovery Details' ) "
          :patient="patient"
          :patientType="_patientType"
          :patientBundle="initialData"
          :patientBundleOATS="OATSpatient"
        />
      </div>
    </div>

    <v-dialog v-model="showDuplicatePrompt">
      <v-card>
        <v-card-title class="modal-title bottom-line pa-0 ma-0">
          <v-row class=" w-100 px-0 mx-0">
            <v-col>
              <h4> Potential Duplicate Profile </h4>
            </v-col>
            <v-spacer />
            <v-col
              cols="auto"
              class=" align-content-center"
            >
              <button
                class="btn v-btn--icon btn-x align-center"
                @click="showDuplicatePrompt = false"
              >
                <span class="x">X</span>
              </button>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text class="modal-body pa-5 font-weight-600">
          {{ duplicateWarningMessage }}
          <p class="mt-1 mb-0">
            Would you like to continue?
          </p>
        </v-card-text>
        <v-card-actions class="top-line pt-3">
          <v-btn
            class="btn btn-secondary"
            @click="showDuplicatePrompt = false"
          >
            Dismiss
          </v-btn>
          <v-spacer />
          <v-btn
            class="btn btn-success"
            @click="postPatientData({ potentialDuplicateConfirmed: true })"
          >
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DebugConsole />
  </div>
</template>

<script>
import DataService from "../services/DataService";
import _ from 'lodash'
import { EXECUTION_ERROR_TYPES, PATIENT_TYPES, DEBUG_MESSAGE_TYPES, SECTION_HANDLER, FHIR_SEVERITY } from "@/constants";
import { useDebugStore } from "@/store/debugStore";
import DebugConsole from "./DebugConsole.vue";
// eslint-disable-next-line
import { mock_patient_bundle, mock_patient_RAM, mock_patient_SMHOne, mock_patient_SMHTwo, mock_patient_SMHThree, 
  // eslint-disable-next-line no-unused-vars
  mock_patient_SMHFour, initialDataMock3, uhnOATSRecipient71099, smhlivingdonor129482 } from '../../data/mocks';
// const debugStore = useDebugStore()
// const environment = process.env.NODE_ENV
export default {
  name: "PatientPage",
  components: {
    DebugConsole,
  },
  data() {
    return {

      sections: [],
      initialData: {
        patient: {},
        entries: [],
        sectionsChecked: [],
      },
      postData: {
        patient: {},
        entries: [],
        sectionsChecked: [],
      },
      patient: {},
      showData: false,
      showSendButton: false,
      postSuccessful: true,
      dataPosted: "no",
      dataPostedErrorMessage: "",
      handlersResponseData: [],
      errorMessages: [],
      showEpicError: false,
      invalidPostRequest: false,
      invalidPostRequestMessage: "Send patient data must be selected",
      showDuplicatePrompt: false,
      duplicateWarningMessage: "",
      potentialDuplicateConfirmed: false,
      postRequestInProgress: false,
      selectedEpisodeOfCare: null,
      episodeOfCareOptions: [],
      debugData: [], // debugData will store a list of all the data statuses.
      debugExpanded: true, // This is to collapse/expand the debug console's body.
      lastAttempt: null, // This will store the last attempt date and time that they were trying to submit the data.
      DEBUG_MESSAGE_TYPES,
      debugStore: useDebugStore(),
      showPreview: false,
    };
  },
  computed: {

    _sourceOptionsChecked() {
      return this.sections.some( ( section ) => section.checked )
    },
    promptDuplicate() {
      //This computed value is used to find if there are any duplicate types
      const potentialDuplicate = this.handlersResponseData && this.handlersResponseData.length > 0
        ? this.handlersResponseData.find( ( item ) => item.execution.result.messages.find( ( message ) => message.type === EXECUTION_ERROR_TYPES.POTENTIAL_DUPLICATE_PROFILE ) )
        : null;
      return potentialDuplicate
    },
    _selectedEpisode() {
      if ( this.episodeOfCareOptions.length > 0 && this.selectedEpisodeOfCare ) {
        const eoc = this.episodeOfCareOptions.find( ( epi ) => epi.value === this.selectedEpisodeOfCare )
        return eoc
      }
      return null
    },
    _patientType() {
      return this._selectedEpisode && this._selectedEpisode.display ? this._selectedEpisode.display : null
    },
    _episodeName() {
      const selected_episode = this._selectedEpisode
      if ( !this._selectedEpisode ) {
        return "NA"
      }
      return selected_episode.title
    },
    _requireEpisodeOfCare() {
      // first get all ids of checked sections
      const checkedSectionIds = this.sections.filter( ( section ) => section.checked ).map( ( sec ) => sec.id );
      if ( checkedSectionIds.length === 0 ) {
        return false
      }
      // check the queries that require to run if they need episode of care as a focus.
      const eoc_queries = this.queries.filter( ( query ) => query.sectionIds.some( ( sectionId ) => checkedSectionIds.includes( sectionId ) )
        && query.searchParams && query.searchParams.focus )
      return eoc_queries.length > 0
    },
    _sendToOATSValidated() {
      // If no options have been checked, then we should not send to OATS.
      if ( !this._sourceOptionsChecked ) {
        return false
      }
      if ( this._requireEpisodeOfCare && !this.selectedEpisodeOfCare ) {
        return false
      }
      return this.showSendButton && !this.postRequestInProgress
    }
  },
  watch: {
    //When duplicate type is found, the prompt duplicate will set the warning message and show the prompt modal.
    promptDuplicate( value ) {
      if ( value ) {
        this.showDuplicatePrompt = true;
        this.duplicateWarningMessage = value.execution.result.messages.find( ( message ) => message.type === EXECUTION_ERROR_TYPES.POTENTIAL_DUPLICATE_PROFILE ).message;
      } else {
        // If it's not found, don't show anyhthing.
        this.showDuplicatePrompt = false;
        this.duplicateWarningMessage = "";
      }
    }
  },
  async mounted() {

    await this.refreshList()
    this.episodeOfCareOptions = await this.fetchEpisodesOfCare( this.patient.id, true );
    
    // If episodeOfCareOptions is returning undefined, null, or something other than array then ensure it is bound to an empty array.
    if ( !Array.isArray( this.episodeOfCareOptions ) ) {
      this.episodeOfCareOptions = []
    }
    // If there is only one episode of care, then automatically select that.
    if ( this.episodeOfCareOptions.length === 1 ) {
      // if there are any options, then automatically select one.
      this.selectedEpisodeOfCare = this.episodeOfCareOptions[0].value;
    }
    if ( this.episodeOfCareOptions.length === 2 ) {
      // if two episodes one disabled and one active, then select active one.
      if( ( this.episodeOfCareOptions[ 0 ].disabled && !this.episodeOfCareOptions[ 1 ].disabled ) || 
      ( !this.episodeOfCareOptions[ 0 ].disabled && this.episodeOfCareOptions[ 1 ].disabled ) ) {
        this.selectedEpisodeOfCare = this.episodeOfCareOptions.filter( ( n ) => ( n.disabled === false ) )[ 0 ]?.value;
      } 
      //if both episodes active, then select first one.
      if( !this.episodeOfCareOptions[ 0 ].disabled && !this.episodeOfCareOptions[ 1 ].disabled ) {
        this.selectedEpisodeOfCare = this.episodeOfCareOptions[0].value;
      }
    }
    await this.fetchFHIRDataAllSections( );
  },

  methods: {

    clearCheckboxes() {
      // clear all selection checks 
      this.sections.forEach( ( section ) => section.checked = false )
    },
    onClick() {
      this.invalidPostRequest = false;
    },
    selectItem( item ) {
      item.checked = !item.checked;
      this.onSelect()

    },
    async fetchEpisodesOfCare( patient_id ) {
      try {
        const isMock = process.env.VUE_APP_MOCK_DATA;
        if ( !patient_id ) {
          throw 'No patient found for episode of care retrieval.'
        }
        const episodeData = await DataService.getEpisodesOfCare( patient_id, isMock === 'true' )
        // If episode of care data is not there or entry does not exist throw the error.
        if ( !episodeData || !episodeData.entry ) {
          throw 'Data returned was not specifically an episode of care data.'
        }
        //Fetch the episode of care entry.
        const formatted_episodes = episodeData.entry.map( ( ent ) => {
          if ( !ent.resource ) {
            throw 'No resources found in episode of care entry'
          }
          // Check if the entry is an error
          if ( Array.isArray( ent.resource.issue ) && ent.resource.issue.length > 0 ) {
            const error = { message: 'Error Fetching EPisode of Care', errors: [] }

            ent.resource.issue.forEach( ( issue ) => {
              error.errors.push( `Error: ${issue.details.text}`, DEBUG_MESSAGE_TYPES.ERROR )
            } )
            this.errorMessages.push( error )
          }
          // fetch valueString of the entry extension of ones with url contains "episode-name"
          const foundEpisodeResource = ent.resource.extension.find( ( ext ) => ext.url && ext.url.includes( 'episode-name' ) );
          const isActive = ent.resource.status === 'active'
          // Fetch the extensions that have a valueCodeableConcept and coding
          const foundValueCodeableConcept = _.filter( ent.resource.extension, ( ext ) => _.get( ext, 'valueCodeableConcept.coding' ) );


          if ( _.isEmpty( foundValueCodeableConcept ) ) {
            throw new Error( 'Data did not have recipient or donor info (valueCodeableConcept)' );
          }
          // go through and find the correct coding type.. currently, only "1"
          const patient_type_data = foundValueCodeableConcept.find( ( vcc ) => {
            return _.find( vcc.valueCodeableConcept.coding, ( coding ) => {
              return coding.code && coding.display && [ "1", "2" ].includes( coding.code );
            } )
          } )
          const allowed_patient_codes = Object.values( PATIENT_TYPES ).map( ( pt ) => pt.code )
          // extract the coding data from the found patient_type data
          const foundCoding = patient_type_data ? patient_type_data.valueCodeableConcept.coding?.find( ( coding ) => {
            return allowed_patient_codes.includes( coding.code );
          } ) : null;
          if ( !foundCoding ) {
            throw new Error( "patient coding not found." )
          }
          // the id of this resource will be the value
          if ( foundEpisodeResource ) {
            return { title: foundEpisodeResource.valueString, value: ent.resource.id, disabled: !isActive, ...foundCoding }
          }
        } )
        return formatted_episodes
      } catch ( err ) {
        console.error( 'Error fetching Episode of Care: ', err )
        // ensure options are defined and is empty.
        this.episodeOfCareOptions = [];
        this.errorMessages.push( err )
        this.addDebugData( err, DEBUG_MESSAGE_TYPES.ERROR )
        this.showEpicError = true;
      }
    },
    clearData() {
      this.patient = {};
      this.OATSpatient = {};
      this.sections = [];
      this.queries = []
      this.dataPosted = "no";
      this.dataPostedErrorMessage = "";
      this.showData = false;
      this.showSendButton = false;
      this.showEpicError = false;
      this.errorMessages = []
      this.invalidPostRequest = false;
      this.handlersResponseData = [];
      this.postRequestInProgress = false;
    },
    async refreshList() {
      this.clearData();
      const sourceQueries = JSON.parse( sessionStorage.getItem( 'sourceQueries' ) )
      this.sections = sourceQueries.sections;
      this.queries = sourceQueries.queries;
      this.sections.sort( ( p1, p2 ) => ( p1.order > p2.order ) ? 1 : ( p1.order < p2.order ) ? -1 : 0 );
      // this.showData = true; 
      if( process.env.VUE_APP_MOCK_DATA === 'true' ) {
      this.patient = mock_patient_SMHFour;
      this.OATSpatient = uhnOATSRecipient71099;
      this.isDonor = this.getPatientType( this.patient );
      console.log( 'this.isDonor', this.isDonor )
      this.tglnId = this.getTglnId( this.patient, this.isDonor );
      console.log( 'this.tglnId', this.tglnId );
      } else {
        await DataService.getPatientDetails()
        .then( ( response ) => {
          this.patient = response;
          this.isDonor = this.getPatientType( this.patient );
          this.tglnId = this.getTglnId( this.patient, this.isDonor );
        } )
        .catch( ( e ) => {
          this.showEpicError = true;
          this.showData = false;
          let responseMessage = e.message ?? e.response?.data?.issue?.[0]?.details?.text ?? '';
          let errorMessage = `Patient details could not be retrieved: ${responseMessage}`;
          if ( e.response?.status ) {
            errorMessage = `${errorMessage} (${e.response.status})`;
          }
          this.addDebugData( errorMessage, DEBUG_MESSAGE_TYPES.ERROR )
          this.errorMessages.push( errorMessage )
        } );
        await DataService.getOATSPatientData( this.tglnId, this.isDonor )
        .then( ( response ) => {
          this.OATSpatient = response?.data;
        } )
        .catch( ( e ) => {
          console.log( 'error', e )
        } );
      }
     
      
      },
    async fetchFHIRDataAllSections( ) {
      this.initialData.entries = [];
      if( process.env.VUE_APP_MOCK_DATA !== 'true' ) { 
      
      const processedQueries = []; // Array to store processed queries and ensure uniqueness
      const queries = structuredClone( this.queries );
     
      
        const checkedSections = this.sections;
    
      
      for ( let section of checkedSections ) {
          this.postData.sectionsChecked.push( section.id ); 
          for ( let query of queries ) {
            // TODO: See the need to fetch a more specific check for uniqueness other than url and description.
            // Check if this query has already been processed before.
            const isAlreadyProcessed = processedQueries.some( ( pc )=> pc.url === query.url && pc.description === query.description )
            if( isAlreadyProcessed ){
              // if already processed, skip this query
              continue;
            }
            if ( query.sectionIds.includes( section.id ) ) {
              query.searched = true;
              // If an episode of care was selected and the query selected contains a search paramter looking for an episode id, then fill that out before sending with the selected episode of care.
              if ( !_.isNil( this.selectedEpisodeOfCare )
                && !_.isEmpty( this.selectedEpisodeOfCare )
                && query.searchParams && query.searchParams.focus ) {
                query.searchParams.focus = this.selectedEpisodeOfCare
                this.initialData.episode_id = this.selectedEpisodeOfCare
              }
              // Whether the query comes back successful or gets caught, it is considered processed.
              processedQueries.push( query );
              await DataService.searchFHIRClient( query )
                .then( ( response ) => {
                  this.initialData.patient = this.patient;
                  if ( response.resourceType === 'Patient' ) {
                    this.initialData.patient = response;
                  }
                  else if( response.entry?.length > 0 ) {
                    this.initialData.entries.push( response.entry );
                  }
              
                } )
                .catch( ( e ) => {
                  this.showEpicError = true;
                  this.showData = false;
                  this.showSendButton = false;
                  let preserveBreaks = false;
                  let httpStatus = null;
                  let errorMessage = { message:`Patient details could not be retrieved: `, errors: [] };
                   let debugErrorDetails = { message: `Patient details could not be retrieved [${this.patient.id || 'NA'}]`,
                    httpStatus: null, 
                    diagnostics:null, 
                    urlCalled:null,
                    errors:[] };
                   
                  if( e.message ){
                    errorMessage += `\n ${e.message}`
                  }
                  //TODO: httpStatus is its own property, if we end up only needing it for debug error details, then directly assign it instead.
                  httpStatus = e.response?.status || null;
                  debugErrorDetails.httpStatus = httpStatus;
                  debugErrorDetails.urlCalled = e.config?.url || null;
                  // If issues are returned from fhir, then traverse and extract the error message(s).
                  if ( e.response.data && Array.isArray( e.response.data.issue ) ) {
                    e.response.data.issue.forEach( ( issue )=>{
                     const errorDetail = {
                        message: null,
                        severity: issue.severity,
                        diagnostics: issue.diagnostics,
                      }
                      if( issue.diagnostics ){
                        debugErrorDetails['diagnostics'] = issue.diagnostics
                      }
                      if( issue.details ){
                        errorDetail.message = `${issue.details.text} [${issue.expression}]`
                        debugErrorDetails.errors.push( errorDetail )
                      }
                      if( issue.severity === FHIR_SEVERITY.FATAL || issue.severity === FHIR_SEVERITY.ERROR ){
                        errorMessage.errors.push( `${issue.details.text} - [${issue.expression}]` )
                      }
                    } )
                    preserveBreaks = true;
                  }

                  this.addDebugData( debugErrorDetails, DEBUG_MESSAGE_TYPES.ERROR, preserveBreaks )
                  this.errorMessages.push( errorMessage )
                } );

          }
        }

      }
      this.showData = true;
    }
      else {
      this.initialData = initialDataMock3;
      this.showData = true;
      }
    
    },
    async fetchFHIRData( ) {
      const processedQueries = []; // Array to store processed queries and ensure uniqueness
      const queries = structuredClone( this.queries );
       // filter out only sections that are checked
     
       const checkedSections = this.sections.filter( ( section ) => section.checked );

      
      for ( let section of checkedSections ) {
          this.postData.sectionsChecked.push( section.id );
          for ( let query of queries ) {
            // TODO: See the need to fetch a more specific check for uniqueness other than url and description.
            // Check if this query has already been processed before.
            const isAlreadyProcessed = processedQueries.some( ( pc )=> pc.url === query.url && pc.description === query.description )
            if( isAlreadyProcessed ){
              // if already processed, skip this query
              continue;
            }
            if ( query.sectionIds.includes( section.id ) ) {
              query.searched = true;
              // If an episode of care was selected and the query selected contains a search paramter looking for an episode id, then fill that out before sending with the selected episode of care.
              if ( !_.isNil( this.selectedEpisodeOfCare )
                && !_.isEmpty( this.selectedEpisodeOfCare )
                && query.searchParams && query.searchParams.focus ) {
                query.searchParams.focus = this.selectedEpisodeOfCare
                this.postData.episode_id = this.selectedEpisodeOfCare
              }
              // Whether the query comes back successful or gets caught, it is considered processed.
              processedQueries.push( query );
              await DataService.searchFHIRClient( query )
                .then( ( response ) => {
                  this.postData.patient = this.patient;
                  if ( response.resourceType === 'Patient' ) {
                    this.postData.patient = response;
                  }
                  else if( response.entry?.length > 0 ) {
                    this.postData.entries.push( response.entry );
                  }
                } )
                .catch( ( e ) => {
                  this.showEpicError = true;
                  this.showData = false;
                  this.showSendButton = false;
                  let preserveBreaks = false;
                  let httpStatus = null;
                  let errorMessage = { message:`Patient details could not be retrieved: `, errors: [] };
                   let debugErrorDetails = { message: `Patient details could not be retrieved [${this.patient.id || 'NA'}]`,
                    httpStatus: null, 
                    diagnostics:null, 
                    urlCalled:null,
                    errors:[] };
                   
                  if( e.message ){
                    errorMessage += `\n ${e.message}`
                  }
                  //TODO: httpStatus is its own property, if we end up only needing it for debug error details, then directly assign it instead.
                  httpStatus = e.response?.status || null;
                  debugErrorDetails.httpStatus = httpStatus;
                  debugErrorDetails.urlCalled = e.config?.url || null;
                  // If issues are returned from fhir, then traverse and extract the error message(s).
                  if ( e.response.data && Array.isArray( e.response.data.issue ) ) {
                    e.response.data.issue.forEach( ( issue )=>{
                     const errorDetail = {
                        message: null,
                        severity: issue.severity,
                        diagnostics: issue.diagnostics,
                      }
                      if( issue.diagnostics ){
                        debugErrorDetails['diagnostics'] = issue.diagnostics
                      }
                      if( issue.details ){
                        errorDetail.message = `${issue.details.text} [${issue.expression}]`
                        debugErrorDetails.errors.push( errorDetail )
                      }
                      if( issue.severity === FHIR_SEVERITY.FATAL || issue.severity === FHIR_SEVERITY.ERROR ){
                        errorMessage.errors.push( `${issue.details.text} - [${issue.expression}]` )
                      }
                    } )
                    preserveBreaks = true;
                  }

                  this.addDebugData( debugErrorDetails, DEBUG_MESSAGE_TYPES.ERROR, preserveBreaks )
                  this.errorMessages.push( errorMessage )
                } );

          }
        }

      }

    },
    async postPatientData( { potentialDuplicateConfirmed = false } = {} ) {
      // add the information that it was pressed to the debug data.
      this.addDebugData( 'Send To OATS clicked, attempting to send to OATS...', DEBUG_MESSAGE_TYPES.DEBUG );
      this.lastAttempt = new Date();
      //if the potential duplicate is confirmed, that means the user has accepted the prompt and we should hide the modal.
      if ( potentialDuplicateConfirmed ) {
        this.showDuplicatePrompt = false;
      }
      this.dataPosted = 'no';
      // [ TPGLI-8669 ] - Need to know if recip or donor down the road, need this data attached.
      this.postData.patientType = this._patientType
      this.handlersResponseData = [];
      await this.prepratePostData();
      if ( this.showEpicError )
        return;
      this.dataPostedErrorMessage = '';
      this.postRequestInProgress = true;

      DataService.postPatientData( this.postData, potentialDuplicateConfirmed )
        .then( ( response ) => {
          this.dataPosted = "success";
          this.postRequestInProgress = false;
          let successMessage = `Successfuly sent patient data to OATS.`
          if( response.data?.message_id ){
            successMessage += ` | Message ID: ${response.data.message_id}`
          } 
          this.addDebugData( successMessage, DEBUG_MESSAGE_TYPES.SUCCESS )
          if ( response.data ) {
            this.handlersResponseData = response.data.results;
          }

        } )
        .catch( ( e ) => {
          this.postRequestInProgress = false;
          this.dataPostedErrorMessage = e.response.data.message;
          this.addDebugData( `[${e.response.status}] ${e.response.data.message}`, DEBUG_MESSAGE_TYPES.ERROR )
          this.dataPosted = "error";
        } );
    },
    addDebugData( data, messageType, preserveBreaks ){
      this.debugStore.addDebugData( data, messageType, preserveBreaks );
    },
    getTglnId( patient, isDonor ) {
      let identifier_type="TGLNRecipient"; // By default, it was already recipient, so keeping it as that for now.
      switch( isDonor ){
        case true:
          identifier_type = "TGLNDonor";
          break;
        case false:
          identifier_type = "TGLNRecipient";
          break;
      }
      let tglnId = patient.identifier.filter( ( n ) => ( n.use === 'usual' && n.type?.text === identifier_type ) ) ;
      return tglnId[0]?.value ? tglnId[0]?.value : '--' ;
    },
    getPatientType( patient ) {
      if ( patient && patient?.identifier && Array.isArray( patient?.identifier )
        && patient?.identifier?.length > 0 && patient?.identifier?.some( ( n ) => n.type?.text === 'TGLNDonor' ) ) {
        return true;
      }
      else {
        return false;
      }
    },
    async prepratePostData() {
      this.postData.patient = {};
      this.postData.entries = [];
      this.postData.sectionsChecked = [];
      await this.fetchFHIRData();
      this.invalidPostRequest = false;
    },
    onSelect() {
      for ( const section of this.sections ) {
        if ( section.checked ) {
          this.showSendButton = true;
          return;
        }
      }
      this.showSendButton = false;
    },
    getHandler( item ) {
      let sections = JSON.parse( sessionStorage.getItem( 'sourceQueries' ) ).sections;
      let displayItem = '';
      if ( SECTION_HANDLER[item] ) {
        displayItem = sections.filter( ( n ) => ( n.id === SECTION_HANDLER[item] ) )[0].title;
      }
      return displayItem;
    },
    isSectionChecked( title ) {
      for ( const section of this.sections ) {
        if ( section.checked && section.title === title ) {
          return true;
        }
      }
    },
  },
};
</script>

<style scoped>
.page-content .summary-card .summary-name {
  padding-left: 12px;
  font-weight: 600;
}

.row {
  padding-left: 12px;
}

.cardcheckbox {
  height: 3.5em;
  max-width: 30em;
  justify-content: center;
  cursor: pointer;
}

.card {
  margin-bottom: .5rem;
}

body .card.cardcheckbox {
  box-shadow: none !important;
}

.v-checkbox {
  color: #212529;
  font-weight: 700;
  font-size: .875rem;
  opacity: 1;
}

/* @media (max-width: 1100px) {
  .v-checkbox  {
  color: #212529;
  font-weight: 500;
  font-size: .875rem;
  opacity: 1;
   }
  } */

:deep(.v-checkbox .v-selection-control) {
  min-height: 5px;
}

.v-card {
  padding: 1%;
  margin-left: 1%;
  margin-right: 1%;
  margin-top: 0.5%;
  margin-bottom: 1%;
}

.v-btn {
  letter-spacing: inherit;
  font-weight: 800;
  font-size: 0.9rem;
  /* height: 3em; */
  /* padding-top: 1em; */
  /* padding-bottom: 1em; */
  /* max-width: 125px; */
}



.v-row {
  padding-left: 5%;
}

.bottom-line {
  border-bottom: 1px solid #C7C8C9;
}

.top-line {
  border-top: 1px solid #C7C8C9;
}

.v-card-text {
  font-family: 'Open Sans', sans-serif;
  color: #212529;
  font-weight: 500;
}

:deep(.v-label) {
  opacity: 1;
  font-size: 1rem;
}

@media (max-width: 1300px) {
  :deep(.v-label) {
    opacity: 1;
    font-size: .8rem;
  }
}

.dataactivitymessage {
  margin-left: 1%;
  margin-bottom: 1rem !important;
  font-size: 1.125rem !important;
}

.x {
  font-size: x-small;
  font-weight: bold;
  vertical-align: middle;
}

.btn-x {
  background-color: transparent;
  border: 2px solid #D2A48C;
  color: #D2A48C !important;
  border-radius: 50%;
  padding-inline: 10px;
  padding-top: 2px;
  padding-bottom: 2px;
}

li::marker {
  font-size: 0.8em;
}

.disable {
  opacity: 0.38;
  pointer-events: none;
}

.connect-input {
  max-width: 30em;
}
</style>
